import type { CSSProperties } from 'react'
import { sharedAssets } from '@shared/assets'
import { cn } from '@shared/ui/lib/utils'
import '../auth-layout.css'

export type BrandLockupPlacement =
  | 'auth-brand'
  | 'auth-frame'
  | 'baseline'
  | 'boot'
  | 'dashboard-header'
  | 'desktop-download'
  | 'landing'

export type BrandLockupTone = 'default' | 'on-dark'

interface BrandLockupProps {
  readonly ariaLabel?: string
  readonly className?: string
  readonly placement: BrandLockupPlacement
  readonly tone?: BrandLockupTone
}

type ResponsiveNumber = number | {
  readonly md?: number
  readonly sm?: number
  readonly xs?: number
}

interface BrandLockupPlacementConfig {
  readonly alovWidth: ResponsiveNumber
  readonly dividerHeight: ResponsiveNumber
  readonly logoWidth: ResponsiveNumber
  readonly spacing: ResponsiveNumber
}

const placements: Record<BrandLockupPlacement, BrandLockupPlacementConfig> = {
  'auth-brand': {
    alovWidth: { sm: 54, xs: 48 },
    dividerHeight: { sm: 40, xs: 36 },
    logoWidth: { sm: 136, xs: 118 },
    spacing: 1.1,
  },
  'auth-frame': {
    alovWidth: { sm: 46, xs: 42 },
    dividerHeight: { sm: 36, xs: 32 },
    logoWidth: { sm: 116, xs: 104 },
    spacing: 1,
  },
  baseline: {
    alovWidth: 40,
    dividerHeight: 34,
    logoWidth: 100,
    spacing: 1,
  },
  boot: {
    alovWidth: 42,
    dividerHeight: 36,
    logoWidth: 104,
    spacing: 1,
  },
  'dashboard-header': {
    alovWidth: 29,
    dividerHeight: 28,
    logoWidth: 76,
    spacing: 0.7,
  },
  'desktop-download': {
    alovWidth: { sm: 62, xs: 52 },
    dividerHeight: { sm: 46, xs: 40 },
    logoWidth: { sm: 150, xs: 126 },
    spacing: 1.25,
  },
  landing: {
    alovWidth: { md: 72, sm: 62, xs: 60 },
    dividerHeight: { md: 52, sm: 45, xs: 42 },
    logoWidth: { md: 166, sm: 142, xs: 136 },
    spacing: { md: 1.5, sm: 1.25, xs: 1 },
  },
}

type BrandLockupStyle = CSSProperties & Record<`--brand-lockup-${string}`, string>

function toPx(value: number) {
  return `${value}px`
}

function toSpacing(value: number) {
  return `${value * 8}px`
}

function assignResponsiveNumber(
  style: Partial<BrandLockupStyle>,
  name: string,
  value: ResponsiveNumber,
  transform: (value: number) => string = toPx,
) {
  if (typeof value === 'number') {
    style[`--brand-lockup-${name}`] = transform(value)
    return
  }

  if (typeof value.xs === 'number') style[`--brand-lockup-${name}-xs`] = transform(value.xs)
  if (typeof value.sm === 'number') style[`--brand-lockup-${name}-sm`] = transform(value.sm)
  if (typeof value.md === 'number') style[`--brand-lockup-${name}-md`] = transform(value.md)
}

function getPlacementStyle(config: BrandLockupPlacementConfig): BrandLockupStyle {
  const style: Partial<BrandLockupStyle> = {}

  assignResponsiveNumber(style, 'logo-width', config.logoWidth)
  assignResponsiveNumber(style, 'alov-width', config.alovWidth)
  assignResponsiveNumber(style, 'divider-height', config.dividerHeight)
  assignResponsiveNumber(style, 'gap', config.spacing, toSpacing)

  return style as BrandLockupStyle
}

/**
 * Presents the two approved marks as one accessible brand relationship.
 * Placement only controls containment; callers keep ownership of routing and layout.
 */
export function BrandLockup({
  ariaLabel = 'Xin Chao Viet Nam and ALOV',
  className,
  placement,
  tone = 'default',
}: BrandLockupProps) {
  const isLanding = placement === 'landing'
  const logoFile = tone === 'on-dark'
    ? 'logo-dark.svg'
    : isLanding
      ? 'logo-landing-navy.svg'
      : 'logo.svg'

  return (
    <span
      aria-label={ariaLabel}
      className={cn('brandLockup', className)}
      data-placement={placement}
      role="img"
      style={getPlacementStyle(placements[placement])}
    >
      <img
        alt=""
        className="brandLockupLogo"
        src={sharedAssets.image(logoFile)}
      />
      <span aria-hidden="true" className="brandLockupDivider" />
      <img
        alt=""
        className="brandLockupAlov"
        src={sharedAssets.image('logo-alov.png')}
      />
    </span>
  )
}
